import { Option, Schema } from 'effect';
import { AttributeTypes, type IAttributeType } from './attribute-types';
import { ComponentTypes, type IComponentType } from './component-types';
import { ConfigurationStatusTypes, type IConfigurationStatusType } from './configuration-status-types';
import type { IGeometryShapeType } from './geometry-shape-types';
import { GEOMETRIES_ROOT, GeometryShapes, GeometryShapeSelf, type IGeometryMeasurement, measurementsOf, SHAPE_PART } from './geometry-shapes';
import { PositiveInteger, Uuid } from './shared';
import { ParameterStateTypes, type IParameterStateType } from './parameter-state-types';
import { SideTypes, type ISideType } from './side-types';
import { TargetTypes, type ITargetType } from './target-types';

/** Where the engine keeps configurations, and what every one of their paths begins with. */
const CONFIGURATIONS_ROOT = '/configurator/configurations/';

/**
 * Where the API names the kinds a parameter can be.
 *
 * A parameter says what it is by pointing at one of these rather than by carrying a word, so this prefix is what a literal has to be spelled against. Written once so the two literals below cannot drift apart.
 */
const CONFIGURATION_TYPES_ROOT = '/configurator/configuration-types';

/**
 * The last segment of a path, which is where this API keeps every identifier.
 *
 * Not exported: what each path's last segment means differs — a configuration's is a uuid, a parameter's is its place among the others, a lookup value's is the code the engine stores — so callers read the getter that says which, and never slice a path themselves.
 */
function lastSegment(self: string): string {
	return self.slice(self.lastIndexOf('/') + 1);
}

/**
 * One segment of a path: anything that is not a separator.
 *
 * What keeps a part from swallowing the rest of the path. Without it a store could be matched as `ydbqa1ab2v/construction-models` and a path with segments to spare would still find something that looked like a key at the end. Measured, not assumed: it does.
 *
 * Used where a segment is only known by its shape. Where the engine has a list — a target, a component, an attribute — that list is used instead, and it rules out the same thing for free.
 */
const Segment = Schema.String.check(Schema.isPattern(/^[^/]+$/));

/** Whether a string can stand as one segment of a path — see {@link Segment} — which is what a key has to be able to do to make a link. */
const isSegment = Schema.is(Segment);

/**
 * A parameter's place among the others, as the API writes it into a path.
 *
 * Digits and nothing else, so a path is refused rather than read as a place that is not one. Kept a string because that is what it is used as — see {@link ConfigurationParameter.index}.
 */
const Index = Schema.String.check(Schema.isPattern(/^\d+$/));

/**
 * Where one parameter lives: under the configuration it belongs to, at its place in the list.
 *
 * The configuration's own uuid is checked here too, which is the point of spelling the whole path out rather than only its tail: a parameter that names a configuration nobody could address is a parameter nobody can change.
 */
const ParameterSelf = Schema.TemplateLiteral([CONFIGURATIONS_ROOT, Uuid, '/parameters/', Index]);

/**
 * How an attribute path is put together, in the order its segments come.
 *
 * Its own constant so that {@link AttributeSelfParts} can be derived from it rather than written out a second time.
 */
const ATTRIBUTE_SELF_PARTS = ['/configurator/targets/', TargetTypes, '/components/', ComponentTypes, '/attributes/', AttributeTypes] as const;

/**
 * What {@link AttributeSelf} decodes a path into: the literals, and the three codes between them.
 *
 * Derived from the parts rather than written out, so reordering the path cannot leave a hand-kept type quietly disagreeing with what the schema actually produces.
 */
type AttributeSelfParts = Schema.TemplateLiteralParser.Type<typeof ATTRIBUTE_SELF_PARTS>;

/**
 * Where an attribute lives, taken apart into what each of its segments means.
 *
 * A parser rather than a pattern, and this is the one place in the domain that decodes a path into its parts instead of slicing it in a getter. Two things fall out of it that nothing else here gives at once: all three codes are checked against the engine's own lists while the string is matched, and the parts arrive
 * already narrowed to those lists — so the getters that read them need no cast, and no unreachable branch to satisfy a cast.
 *
 * Three vocabularies, and they must stay separate: a target holds components, and a component holds attributes. `modl` is a {@link TargetTypes} and `mdl` a {@link ComponentTypes}, and the two sit four segments apart in the same path.
 *
 * It costs the field its shape in memory: `attribute.self` holds the parts and not the string. That is why {@link ConfigurationParameter.attributePath} exists, and why nothing else should reach into the tuple. Encoding puts the string back exactly as it came, which is what a change sends back.
 *
 * The assertion is the price of checking all three, and it hides nothing about values. Left to itself the parser types its _encoded_ side as a template literal — and TypeScript expands that eagerly, so 31 targets by 224 components by 170 attributes is a union of 1,180,480 members and the compiler gives up with TS2590.
 * It compiled while the target was any string; adding the third list is what crossed the line. Widening that side to `string` is what it was going to be used as anyway: it is the string that goes on the wire.
 *
 * Nothing else is widened. The decoded side keeps the three narrow unions, derived from the same parts, and the runtime is untouched — the assertion is erased, and the schema still refuses a code that is not on its list.
 */
const AttributeSelf = Schema.TemplateLiteralParser(ATTRIBUTE_SELF_PARTS) as never as Schema.Codec<AttributeSelfParts, string>;

/** How a state path is put together. See {@link ATTRIBUTE_SELF_PARTS} for why it is a constant. */
const STATE_SELF_PARTS = ['/configurator/configuration-parameter-states/', ParameterStateTypes] as const;

/**
 * Which state a parameter is in, taken apart the way {@link AttributeSelf} is.
 *
 * A parser and not a plain {@link Schema.TemplateLiteral}, for the same reason: the state is worth narrowing and not only checking, so {@link ConfigurationParameter.stateType} needs no cast.
 *
 * No assertion is needed here, unlike on the attribute path. Three states make an encoded type of three members, which TypeScript represents without complaint.
 */
const StateSelf = Schema.TemplateLiteralParser(STATE_SELF_PARTS);

/** Where {@link StateSelf} puts the state once it has been taken apart. */
const STATE_PART = 1;

/** The state of a parameter in the `default` state, as {@link StateSelf} holds it taken apart. What a pending parameter is sent as once it is answered — see {@link answeredState}. */
const DEFAULT_STATE = { self: [STATE_SELF_PARTS[0], 'default'] } as const;

/**
 * The state a parameter goes back to the engine in once the user has changed it: the state it came in, with `pending` written as `default`.
 *
 * The engine's rule, agreed with the people behind it: a parameter's state is not the client's to choose. What came as `default` goes back as `default` and what came as `override` as `override`, whether or not the user changed the value — what a value the user chose means to the engine is the engine's to say. The one
 * state the client writes is `pending`, which the engine gives a parameter it has yet to settle: answered, it goes back as `default`, and unanswered it is not sent at all — see {@link answeredOf}. Every `with` method on the classes below sends its parameter in this state, and nothing else writes a state.
 */
function answeredState(parameter: AnsweredParameter): AnsweredParameter['state'] {
	return parameter.stateType === 'pending' ? DEFAULT_STATE : parameter.state;
}

/**
 * What {@link answeredState} reads off a parameter: the state it came in, and that state taken apart.
 *
 * A shape rather than a class, because two families of parameter carry it — a model's and a stock item's — and the rule is the engine's for both.
 */
interface AnsweredParameter {
	readonly state: { readonly self: readonly [(typeof STATE_SELF_PARTS)[0], IParameterStateType] };
	readonly stateType: IParameterStateType;
}

/** Where {@link AttributeSelf} puts the target once it has been taken apart. */
const TARGET_TYPE_PART = 1;

/** Where {@link AttributeSelf} puts the component type once it has been taken apart. */
const COMPONENT_TYPE_PART = 3;

/** Where {@link AttributeSelf} puts the attribute type once it has been taken apart. */
const ATTRIBUTE_TYPE_PART = 5;

/**
 * What every parameter carries, whatever kind it is.
 *
 * Not exported: nothing takes a parameter that has not said what kind it is, because there is no reading a value without knowing whether it is a code to be looked up or text the user typed. Callers take {@link IConfigurationParameter} and tell the two apart by the classes themselves.
 *
 * The API answers with more — the collection a parameter's side was picked from, for one. Only what the Configurator acts on is declared, the way `Calculation` declares only what it uses, so a change to a field nobody reads cannot fail a call.
 */
class ConfigurationParameter extends Schema.Class<ConfigurationParameter>('ConfigurationParameter')(
	Schema.Struct({
		attribute: Schema.Struct({
			name: Schema.NonEmptyString,
			self: AttributeSelf,
		}),
		isCoupled: Schema.Boolean,
		self: ParameterSelf,
		state: Schema.Struct({ self: StateSelf }),
	}),
) {
	/**
	 * What to call the parameter on screen.
	 *
	 * The engine writes it in Danish and already spelled for a user — `Profilsæt: Navn` — so there is nothing to translate, and no list of labels to keep in step with the engine's.
	 *
	 * Read off the attribute rather than held beside it, so a parameter cannot be built with a name that disagrees with the attribute it names.
	 */
	public get label(): string {
		return this.attribute.name;
	}

	/**
	 * Where the parameter sits among the others, as the API spells it.
	 *
	 * The last segment of `self` is its place — `.../parameters/3` is the fourth — which is what the API addresses it by and what makes it distinct from its neighbours. A string rather than a number, because it is used to build a path and never counted with.
	 */
	public get index(): string {
		return lastSegment(this.self);
	}

	/**
	 * How the parameter got the value it has.
	 *
	 * Not `state`, which is the field the engine sends and is a path rather than a word. The name follows {@link ConfigurationParameter.targetType} and its two neighbours: each of them says which of the engine's vocabularies a segment was read against.
	 */
	public get stateType(): IParameterStateType {
		return this.state.self[STATE_PART];
	}

	/**
	 * What the parameter is about at all: the model as a whole, or one sash of it.
	 *
	 * The outermost of the three things the attribute path says. Two parameters can name the same component and the same attribute and still be different parameters — the profile set of the first sash and of the second — and this is what tells them apart.
	 */
	public get targetType(): ITargetType {
		return this.attribute.self[TARGET_TYPE_PART];
	}

	/**
	 * Which part of the window the parameter configures.
	 *
	 * `pfs` is the profile set, `krm` the frame, `ram` the sash. Read off the attribute's path, where it is the only place the engine says it — a parameter carries no field for it.
	 *
	 * Already narrowed to {@link ComponentTypes} with no cast, because the path was taken apart against that list rather than sliced out of a string afterwards. See {@link AttributeSelf}.
	 */
	public get componentType(): IComponentType {
		return this.attribute.self[COMPONENT_TYPE_PART];
	}

	/**
	 * Which property of that part the parameter sets.
	 *
	 * `nn` is a name, `hd` a height. The engine's shorthand rather than anything to show a user — {@link ConfigurationParameter.label} is what they read — so this is for grouping parameters and deciding what kind of control one gets, not for putting on screen.
	 */
	public get attributeType(): IAttributeType {
		return this.attribute.self[ATTRIBUTE_TYPE_PART];
	}

	/**
	 * The attribute's path as the engine wrote it.
	 *
	 * Put back together rather than kept, because {@link AttributeSelf} holds the path in parts. Here so that nothing outside this class has to know it does: a caller that needs the path asks for the path.
	 */
	public get attributePath(): string {
		return this.attribute.self.join('');
	}

	/**
	 * What tells this parameter from every other in a configuration, and finds it again in the next.
	 *
	 * A changed configuration is a new one, at a path of its own, and its parameters are new objects at new paths — see {@link ParameterSelf}, which carries the configuration's uuid. So neither the object nor `self` can say that a parameter in the answer is the one a row was showing. The attribute path can: it names
	 * what the parameter is about, the target, the component and the attribute, and that is what the two have in common. Unique in a configuration as far as the engine has shown, with the one open question being a side — two parameters about the same attribute on two sides would share it, and the side would have to
	 * join it here, and nowhere else.
	 */
	public get identity(): string {
		return this.attributePath;
	}
}

/**
 * Where the engine keeps the collections a lookup is picked from.
 *
 * Note the `/configurator` prefix, and note that {@link ModelSelf} has none. The two are not a mistake and not ours to tidy up: the engine writes its own links prefixed and expects a model named unprefixed. Recording both here is what keeps someone from "fixing" one to match the other.
 */
const STORES_ROOT = '/configurator/apps/one/stores/';

/**
 * Where one collection of lookup values lives.
 *
 * The store and the collection are checked for being segments and for nothing else. Both are tempting to pin down — every store seen so far is written `engine-` and something, and the six collections seen so far have names we could list — but a rule taken from what one response happened to contain is a guess, and a
 * guess here fails a call. {@link ComponentTypes} is strict because it is a backend enum we were handed in full; this is not that.
 *
 * Used exactly as it arrives and never rebuilt, so what this buys is drift showing up here rather than as a request to a path that does not resolve.
 */
const ValuesSelf = Schema.TemplateLiteral([STORES_ROOT, Segment, '/', Segment]);

/**
 * Where one chosen lookup value lives: in one of those collections, under its own code.
 *
 * That this is the collection in {@link ValuesSelf} with one segment on the end is the engine's arrangement and not something a schema can say, so the two are checked apart. A value from some other collection would decode; it would be the engine contradicting itself, not us.
 */
const ValueSelf = Schema.TemplateLiteral([STORES_ROOT, Segment, '/', Segment, '/', Segment]);

/** How a side path is put together. See {@link ATTRIBUTE_SELF_PARTS} for why it is a constant. */
const SIDE_SELF_PARTS = ['/configurator/configuration-sides/', SideTypes] as const;

/**
 * Which side a parameter applies to, taken apart the way {@link AttributeSelf} is.
 *
 * A parser, because the side is worth narrowing and not only checking — which way round a fitting goes is something a control has to show. Seventeen sides make an encoded type of seventeen members, so no assertion is needed here.
 */
const SideSelf = Schema.TemplateLiteralParser(SIDE_SELF_PARTS);

/** Where {@link SideSelf} puts the side once it has been taken apart. */
const SIDE_PART = 1;

/**
 * A parameter whose value is picked from a list the engine keeps.
 *
 * The list is not part of the configuration: `values` points at the collection it is read from, and that collection is fetched on its own and searched — there are six of them across a configuration and they run to thousands of entries, so none of them is worth fetching until the user opens one.
 *
 * `values` is used exactly as it arrives. The engine writes these links with the `/configurator` prefix and with the store spelled differently than a model search spells it, so rebuilding one from its parts would produce a path that does not resolve.
 *
 * `value` may be missing. A lookup nobody has answered yet — one the engine sends in the `pending` state, with the question still open — has no chosen value to link to, and the engine leaves the key out rather than pointing at nothing. `optionalKey` is what says that, the way it says it for `side`, and it is what
 * sends the parameter back as it came: a change to another parameter must not invent an answer to this one. A {@link StringParameter} is never without a value, since an unfilled string is an empty one, so this is the one kind the key is optional on.
 */
export class LookupParameter extends ConfigurationParameter.extend<LookupParameter>('LookupParameter')(
	Schema.Struct({
		side: Schema.optionalKey(Schema.Struct({ self: SideSelf })),
		type: Schema.Struct({ self: Schema.Literal(`${CONFIGURATION_TYPES_ROOT}/lookup`) }),
		value: Schema.optionalKey(Schema.Struct({ self: ValueSelf })),
		values: Schema.Struct({ self: ValuesSelf }),
	}),
) {
	/**
	 * Which side of the window the parameter applies to, or null when it applies to no one side.
	 *
	 * Only some parameters have one — the hardware of a frame field does, its surface treatment does not — so the key is absent rather than null on the wire, and `optionalKey` is what says that. Answered as null here because absent and "no side" are the same thing to read.
	 *
	 * On a lookup and not on a {@link StringParameter}, which is the engine's arrangement: a side is something a fitting picked from a list is mounted on, and text the user types is not mounted anywhere.
	 *
	 * The engine sends a `sides` link beside it, pointing at the collection this was picked from. It is not declared: the list is the same seventeen for every parameter and is already here as {@link SideTypes}, so fetching it would be asking the engine for what we have.
	 */
	public get sideType(): ISideType | null {
		return this.side === undefined ? null : this.side.self[SIDE_PART];
	}

	/**
	 * The code the chosen value is stored under, or null while no value has been chosen.
	 *
	 * What the engine keeps, and not what the user reads: the name beside it lives on the value in the collection `values` points at, which is fetched separately. Until it has been, this is all there is to show — so a picker shows the code and replaces it with the name once the collection is in.
	 *
	 * Null for a lookup nobody has answered, which the engine sends without a value — see the class. Null rather than an empty code, the way {@link LookupParameter.sideType} answers for an absent side: there is no code, not a code of nothing, and a picker seeded with nothing is what shows that.
	 */
	public get key(): string | null {
		return this.value === undefined ? null : lastSegment(this.value.self);
	}

	/**
	 * The same lookup answered with that key, or null when the key would not make a link: the value is the key under the collection the parameter is picked from, and the state is the one the engine gave it, with `pending` written as `default` — see {@link answeredState}.
	 *
	 * Picked or typed, the row hands over the key either way. The link is the key under `values`, which is exactly the shape {@link ValueSelf} spells out, and a key that is not one segment of a path — nothing, which is what a field emptied and left hands over, or something with a separator in it — makes no link at all.
	 * That is answered with null rather than refused by the class, for the reason {@link GeometryParameter.withMeasurement} answers null for a length no measurement can be: a change the parameter cannot have is sent as nothing, where a class throwing would stop the row's event half way and leave the error uncaught. A
	 * key the store has no value for is another matter: it makes a well-formed link, which the API then refuses.
	 *
	 * Spread rather than listed field by field, unlike {@link GeometryParameter.withMeasurement}, because two of the keys may be absent: the side, on most parameters, and the value, on one nobody has answered — which is exactly what this answers. Listed, an absent side would be written as undefined and sent as a side
	 * of nothing; spread, it stays left out and goes back as it came.
	 */
	public withKey(key: string): LookupParameter | null {
		if (!isSegment(key)) {
			return null;
		}
		return new LookupParameter({ ...this, state: answeredState(this), value: { self: `${this.values.self}/${key}` } });
	}

	/**
	 * The same lookup applying to that side instead, in the state {@link LookupParameter.withKey} sends a changed parameter in — unless nobody has answered it.
	 *
	 * The side is held in parts — see {@link SideSelf} — so it is written in parts, and put together again only on the wire: `/configurator/configuration-sides/T`. Spread for the reason given there too: a lookup nobody has answered must not gain a value of undefined on the way to a side.
	 *
	 * A side is where the chosen value is mounted, not an answer to which value it is, so a lookup without a value stays `pending` with its new side. Written as `default`, it would pass {@link answeredOf} and go to the engine as an answer that answers nothing; left pending, it is left out until a value is picked.
	 */
	public withSide(side: ISideType): LookupParameter {
		return new LookupParameter({ ...this, side: { self: [SIDE_SELF_PARTS[0], side] }, state: this.value === undefined ? this.state : answeredState(this) });
	}
}

/**
 * A parameter whose value is text the user types.
 *
 * `Schema.String` and not `Schema.NonEmptyString`: an empty one is what an optional parameter looks like when nobody has filled it in, and the engine answers with them.
 */
export class StringParameter extends ConfigurationParameter.extend<StringParameter>('StringParameter')(
	Schema.Struct({
		type: Schema.Struct({ self: Schema.Literal(`${CONFIGURATION_TYPES_ROOT}/string`) }),
		value: Schema.String,
	}),
) {
	/** The same parameter answered with that text, in the state {@link LookupParameter.withKey} sends a changed parameter in. */
	public withValue(value: string): StringParameter {
		return new StringParameter({ ...this, state: answeredState(this), value });
	}
}

/**
 * A parameter whose value the engine works out from the others.
 *
 * `Mullion 2: Y-coordinate` is one: where a post sits follows from the frame around it. The engine sends what it arrived at, and sends it as text like a {@link StringParameter} does — `"0.0"` rather than `0` — so that is what is declared here. One example is not a rule, and the wire says string.
 *
 * Identical to {@link StringParameter} but for the kind it names, and kept a class of its own rather than folded in with it, because the classes are how the Configurator tells its kinds apart: `instanceof` is what a renderer branches on, and two kinds sharing one class cannot be branched on at all. What differs is not
 * the shape but what the value means — one is typed, the other is derived — and that is a difference the day it stops being edited the same way.
 */
export class ExpressionParameter extends ConfigurationParameter.extend<ExpressionParameter>('ExpressionParameter')(
	Schema.Struct({
		type: Schema.Struct({ self: Schema.Literal(`${CONFIGURATION_TYPES_ROOT}/expression`) }),
		value: Schema.String,
	}),
) {
	/** The same parameter answered with that text, the way a {@link StringParameter} is, since the two are edited the same way for now. */
	public withValue(value: string): ExpressionParameter {
		return new ExpressionParameter({ ...this, state: answeredState(this), value });
	}
}

/** A shape as the engine writes it, and back: what {@link GeometryParameter.withMeasurement} changes one measurement of. */
const encodeGeometryShape = Schema.encodeSync(GeometryShapes);
const decodeGeometryShape = Schema.decodeUnknownOption(GeometryShapes);

/**
 * A parameter whose value is the shape the window is cut to, and the measurements that shape takes.
 *
 * Not picked from a store like a {@link LookupParameter}: `values` points at the one list of shapes the engine has, which is the same for every model and already here as {@link GeometryShapeTypes}. So it is pinned to that path rather than checked for being one of many.
 *
 * The value is one of the shape classes, told apart by the shape it names — see {@link GeometryShapes} — so it holds exactly the measurements that shape has. `measurementsOf` reads them off it in the engine's order, and a reader that needs one shape in particular narrows it with `instanceof`.
 *
 * Not answered in the answers list: the row shows the code the engine writes beside the shape, and changing it is the geometry dialog's job.
 */
export class GeometryParameter extends ConfigurationParameter.extend<GeometryParameter>('GeometryParameter')(
	Schema.Struct({
		type: Schema.Struct({ self: Schema.Literal(`${CONFIGURATION_TYPES_ROOT}/geometry`) }),
		value: GeometryShapes,
		values: Schema.Struct({ self: Schema.Literal(GEOMETRIES_ROOT) }),
	}),
) {
	/** Which shape the window is cut to. Already narrowed, because every shape class takes its path apart against its own literal. */
	public get shapeType(): IGeometryShapeType {
		return this.value.shape.self[SHAPE_PART];
	}

	/**
	 * The code the engine writes beside the shape — `1A` for a rectangle — which is what the answers list shows.
	 *
	 * Read off the value rather than held beside it, since that is where the engine puts it, so a parameter cannot show a code that disagrees with the shape it came with.
	 */
	public get vip(): string {
		return this.value.vip;
	}

	/**
	 * The same geometry with one measurement given another length, or null when it cannot have that one.
	 *
	 * Everything but the one measurement is the parameter's own, the shape, the other measurements and the state included, so the engine reads it as this parameter answered again — the way a changed answer keeps the rest of its parameter as it was. The state goes back as it came, which is the engine's rule for every
	 * kind; see {@link answeredState}.
	 *
	 * Null for a measurement the shape does not have, which is checked here rather than left to decoding: a shape class ignores a measurement it does not take, so a rise put on a rectangle would be dropped without a word and sent as no change at all. Null as well for a length that is not one a measurement can be —
	 * nought, a fraction — which the shape refuses the way it refuses one read off the engine.
	 */
	public withMeasurement(measurement: IGeometryMeasurement, value: number): GeometryParameter | null {
		if (!measurementsOf(this.value).some((each) => each.measurement === measurement)) {
			return null;
		}
		const shape = decodeGeometryShape({ ...encodeGeometryShape(this.value), [measurement]: value });
		if (Option.isNone(shape)) {
			return null;
		}
		return new GeometryParameter({
			attribute: this.attribute,
			isCoupled: this.isCoupled,
			self: this.self,
			state: answeredState(this),
			type: this.type,
			value: shape.value,
			values: this.values,
		});
	}
}

/**
 * A parameter in whichever of the four kinds the Configurator can present.
 *
 * Exported as the schema and not only as the type, because {@link Configuration} decodes with it.
 *
 * Four kinds and not every kind the engine has, deliberately, and this is the one place the Configurator refuses what it does not understand rather than declaring it. `Calculations` names all four statuses so an unread one cannot fail a call — but a status is something to report, where a parameter is something to
 * edit. A kind we cannot edit has no control to put on screen, so decoding it would buy a configuration that silently cannot be saved back. Failing here says which kind arrived and where, which is what adding the next kind starts from — and it is how this third one was found, since a configuration carrying one refused
 * to decode whole.
 */
export const ConfigurationParameters = Schema.Union([ExpressionParameter, GeometryParameter, LookupParameter, StringParameter]);

/** A parameter in whichever of the four kinds the Configurator can present. */
export type IConfigurationParameter = typeof ConfigurationParameters.Type;

/**
 * A geometry parameter cut to a shape the user picked, sent for the engine to measure.
 *
 * Sent and never read, and that is why it is a class apart from {@link GeometryParameter}. A shape read off the engine comes with its measurements, and {@link GeometryShapes} refuses one without them — a configuration missing a measurement is one the engine has contradicted itself in. A shape the user has just picked
 * has none yet: the engine works them out from the shape and the width and height the configuration is made with, and answers with a {@link GeometryParameter} that has them. So this carries the shape alone, from the whole list of shapes rather than one class per shape.
 *
 * Not one of {@link ConfigurationParameters}, so nothing read off the engine can decode as one. It goes out among them — see {@link SentConfigurationParameters}.
 */
export class PickedGeometryParameter extends ConfigurationParameter.extend<PickedGeometryParameter>('PickedGeometryParameter')(
	Schema.Struct({
		type: Schema.Struct({ self: Schema.Literal(`${CONFIGURATION_TYPES_ROOT}/geometry`) }),
		value: Schema.Struct({ shape: Schema.Struct({ self: GeometryShapeSelf }) }),
		values: Schema.Struct({ self: Schema.Literal(GEOMETRIES_ROOT) }),
	}),
) {
	/**
	 * The geometry parameter cut to another shape: the same parameter, at the same place, with the picked shape and no measurements.
	 *
	 * Everything but the value is the parameter's own, the state included, so the engine reads it as that parameter answered again — the way a changed answer keeps the rest of its parameter as it was. The state goes back as it came, which is the engine's rule for every kind; see {@link answeredState}.
	 */
	public static from(parameter: GeometryParameter, shapeType: IGeometryShapeType): PickedGeometryParameter {
		return new PickedGeometryParameter({
			attribute: parameter.attribute,
			isCoupled: parameter.isCoupled,
			self: parameter.self,
			state: answeredState(parameter),
			type: parameter.type,
			value: { shape: { self: [`${GEOMETRIES_ROOT}/`, shapeType] } },
			values: parameter.values,
		});
	}

	/** Which shape was picked. */
	public get shapeType(): IGeometryShapeType {
		return this.value.shape.self[SHAPE_PART];
	}
}

/**
 * A parameter in whichever of the kinds that can be sent back: the ones that are read, and a geometry picked for the engine to measure.
 *
 * What a configuration is made from. Wider than {@link ConfigurationParameters} by {@link PickedGeometryParameter} alone, which is only ever sent.
 */
export const SentConfigurationParameters = Schema.Union([ConfigurationParameters, PickedGeometryParameter]);

/** A parameter in whichever of the kinds that can be sent back. */
export type ISentConfigurationParameter = typeof SentConfigurationParameters.Type;

/**
 * The parameter that says which shape the window is cut to, or null when the parameters have none.
 *
 * None is the ordinary case for a model without a geometry, and for the empty list of a position that is not a configured model. Found by its class rather than by its attribute, so whatever the engine calls it, the one that holds a shape is the one that is found.
 */
export function findGeometryParameter(parameters: ReadonlyArray<IPresentedParameter>): GeometryParameter | null {
	return parameters.find((parameter): parameter is GeometryParameter => parameter instanceof GeometryParameter) ?? null;
}

/**
 * The parameters that go to the engine: every one but those nobody has answered.
 *
 * A parameter the engine gave as `pending` is a question it has asked and not had answered. Sent back unanswered it would be the client asking the engine its own question, which the engine does not take; left out, the engine asks it again in its answer, still pending, until the user answers it. Answered, it is
 * `default` by then — see {@link answeredState} — and goes with the rest. The rest go as they are: a changed one is already in the state it is to be sent in.
 */
export function answeredOf<P extends { readonly stateType: IParameterStateType }>(parameters: ReadonlyArray<P>): Array<P> {
	return parameters.filter((parameter) => parameter.stateType !== 'pending');
}

/**
 * Where one configuration lives: the root, and the uuid it is known by.
 *
 * `Schema.TemplateLiteral` and not the {@link AttributeSelf} parser, because there is nothing to gain by taking it apart — a uuid decodes to a plain string, so the parts would be no better typed than the whole. The rule is that the parser is for a path whose segments are worth narrowing, and this is for a path that is
 * only worth checking. So `self` stays the string it arrived as.
 *
 * It checks more than it looks like it does, and this was measured rather than assumed: the `isUUID` check on the part is applied while the string is matched, and a path with anything after the uuid is refused. So a parameter's own path — the same root with `/parameters/0` on the end — cannot be mistaken for a
 * configuration's.
 */
export const ConfigurationSelf = Schema.TemplateLiteral([CONFIGURATIONS_ROOT, Uuid]);

/**
 * Which construction model the configuration is of.
 *
 * Written without the `/configurator` prefix, unlike every other path in this file, and that is the engine's doing rather than an oversight: a model search answers with the prefixed form, and the POST that makes a configuration refuses it. The response then names the model unprefixed too, so this is the one shape that
 * both reads and writes — which is exactly why it is pinned down here instead of being left a string for whoever builds the request body to get right on their own.
 *
 * Exported, unlike the other path schemas here, because it is the one that is also written rather than only read: the POST that makes a configuration names the model with it. One schema for both directions is what stops a request being built to a shape the response would refuse.
 *
 * The store is written differently here as well — plain, where {@link STORES_ROOT} paths carry an `engine-` in front of the same store. Left as a segment for the reason given there.
 */
export const ModelSelf = Schema.TemplateLiteral(['/apps/one/stores/', Segment, '/construction-models/', Segment]);

/** How a construction model is named, both when the engine names one and when we do. */
export type IModelReference = typeof ModelSelf.Type;

/** How a status path is put together. See {@link ATTRIBUTE_SELF_PARTS} for why it is a constant. */
const STATUS_SELF_PARTS = ['/configurator/configuration-statuses/', ConfigurationStatusTypes] as const;

/** Whether the engine will price the configuration, taken apart the way {@link StateSelf} is: two statuses are worth narrowing, since the machine branches on them. */
const StatusSelf = Schema.TemplateLiteralParser(STATUS_SELF_PARTS);

/** Where {@link StatusSelf} puts the status once it has been taken apart. */
const STATUS_PART = 1;

/**
 * A model configured for one position: what the engine will build, parameter by parameter.
 *
 * Made by asking the engine for a model's defaults and changed by putting it back, so what is declared here is both what is read and what is sent. That is why `parameters` keeps the envelope the API wraps its items in rather than being unwrapped to an array: a collection that came back as `{ items: [...] }` has to go
 * back the same way, and a shape that is decoded one way and encoded another is a shape with two definitions of itself.
 *
 * The width, the height and the quantity are the position's own, sent with every configuration it is asked for and answered back: what the engine may configure depends on how big the window is, and the calculation that prices the configuration reads how many of it are wanted from here.
 */
export class Configuration extends Schema.Class<Configuration>('Configuration')(
	Schema.Struct({
		height: PositiveInteger,
		model: Schema.Struct({ self: ModelSelf }),
		parameters: Schema.Struct({ items: Schema.Array(ConfigurationParameters) }),
		quantity: PositiveInteger,
		self: ConfigurationSelf,
		status: Schema.Struct({ self: StatusSelf }),
		width: PositiveInteger,
	}),
) {
	/**
	 * Whether the engine will price the configuration: `complete` when every parameter has an answer, `pending` while one has not.
	 *
	 * Required rather than optional, although the engine only began sending it on 9 October 2026: a configuration that did not say would be one nobody could tell whether to price, and the engine always says now. Read by the machine the moment a configuration lands, to price the position or to leave it waiting — see
	 * `ConfiguratorMachine` — and by the position's row, to say which.
	 */
	public get statusType(): IConfigurationStatusType {
		return this.status.self[STATUS_PART];
	}

	/**
	 * The identifier a later change addresses the configuration by.
	 *
	 * The API only spells it out as the last segment of `self`, the way a construction model spells its key. Derived here so no caller slices the path itself.
	 */
	public get id(): string {
		return lastSegment(this.self);
	}
}

/**
 * Where the engine keeps the attributes a stock item's parameters set.
 *
 * Not the attributes a model's parameters set: those sit under a target and a component — see {@link AttributeSelf} — and a stock item has neither, so its attributes hang straight off a root of their own.
 */
const STOCK_ATTRIBUTES_ROOT = '/configurator/stock-attributes/';

/**
 * Where one stock attribute lives: under {@link STOCK_ATTRIBUTES_ROOT}, by its code — `KMP`, `MBR`.
 *
 * The code is checked for being a segment and nothing else, for the reason {@link ValuesSelf} gives: no list of stock attributes was handed over, and a rule taken from the four one answer happened to contain is a guess that fails a call.
 */
const StockAttributeSelf = Schema.TemplateLiteral([STOCK_ATTRIBUTES_ROOT, Segment]);

/**
 * What every parameter of a stock configuration carries, whatever kind it is.
 *
 * Its own family rather than the model's {@link ConfigurationParameter}, for two things that differ. The attribute is a stock attribute, which the model's attribute path would refuse. And the engine says of each one whether the user may change it at all — `isReadOnly` — which no model parameter carries.
 *
 * Not exported, for the reason the model's is not: nothing takes a parameter that has not said what kind it is.
 */
class StockConfigurationParameter extends Schema.Class<StockConfigurationParameter>('StockConfigurationParameter')(
	Schema.Struct({
		attribute: Schema.Struct({
			name: Schema.NonEmptyString,
			self: StockAttributeSelf,
		}),
		isCoupled: Schema.Boolean,
		isReadOnly: Schema.Boolean,
		self: ParameterSelf,
		state: Schema.Struct({ self: StateSelf }),
	}),
) {
	/** What to call the parameter on screen: the attribute's name, which the engine writes in Danish and spelled for a user — `Modelbredde`. See {@link ConfigurationParameter.label}. */
	public get label(): string {
		return this.attribute.name;
	}

	/** Where the parameter sits among the others, as the API spells it. See {@link ConfigurationParameter.index}. */
	public get index(): string {
		return lastSegment(this.self);
	}

	/** How the parameter got the value it has. See {@link ConfigurationParameter.stateType}. */
	public get stateType(): IParameterStateType {
		return this.state.self[STATE_PART];
	}

	/** Which attribute the parameter sets, by the engine's code for it — `MBR` is the model width. The engine's shorthand rather than anything to show a user: {@link StockConfigurationParameter.label} is what they read. */
	public get attributeType(): string {
		return lastSegment(this.attribute.self);
	}

	/**
	 * What tells this parameter from every other in a configuration, and finds it again in the next.
	 *
	 * The attribute's path, for the reason {@link ConfigurationParameter.identity} gives: a changed configuration is a new one, and its parameters new objects at new paths, but what a parameter is about stays the same. A stock item's attributes are one level deep, so the path is the attribute's code under its root.
	 */
	public get identity(): string {
		return this.attribute.self;
	}
}

/**
 * Where one answer a stock attribute can be given lives: among the store's attribute answers, under its own code.
 *
 * Written prefixed and with the store spelled `engine-`, the way the engine links its own collections. Checked for its shape only, for the reason {@link ValuesSelf} gives.
 */
const StockAnswerSelf = Schema.TemplateLiteral([STORES_ROOT, Segment, '/component-inventory-item-attribute-answers/', Segment]);

/**
 * One of the answers a stock attribute can be given, as the engine offers them beside the parameter.
 *
 * The engine names each by a code — the last segment of `self` — and that code is the answer: what a parameter's `value` holds once it is given this one. It is written into the path encoded, so `Grå` is `Gr%C3%A5` there, and it goes back exactly as it came — see {@link StockAnswer.key} and {@link StockAnswer.label}.
 *
 * `sortOrder` is the order the engine wants them offered in, and `isDefault` the one to take when nobody has answered. The `name` beside the code is the engine's longer word for it, which is not shown: the column is too narrow for it.
 */
export class StockAnswer extends Schema.Class<StockAnswer>('StockAnswer')(
	Schema.Struct({
		isDefault: Schema.Boolean,
		name: Schema.String,
		self: StockAnswerSelf,
		sortOrder: Schema.Finite,
	}),
) {
	/** The code as the engine wrote it into the path — `Gr%C3%A5` — which is what a parameter is answered with, since it is what the engine handed over. */
	public get key(): string {
		return lastSegment(this.self);
	}

	/**
	 * The code as a user reads it — `Grå` — which is what the answer is shown as.
	 *
	 * Decoded from {@link StockAnswer.key}. A code that does not decode — a stray `%` the engine did not mean as an escape — is shown as it was written rather than not at all.
	 */
	public get label(): string {
		try {
			return decodeURIComponent(this.key);
		} catch {
			return this.key;
		}
	}

	/** Whether a parameter holding that value has been given this answer: by its code as written or as read, since either may be what the engine sends back. */
	public isAnswerTo(value: string): boolean {
		return value === this.key || value === this.label;
	}
}

/**
 * The answers a stock attribute can be given, offered beside the parameter, and where the engine keeps them.
 *
 * The whole list comes with the parameter, so it is never fetched: `self` is kept only so the parameter goes back as it came.
 */
const StockAnswers = Schema.Struct({ items: Schema.Array(StockAnswer), self: Schema.NonEmptyString });

/**
 * A stock parameter whose value is text.
 *
 * `value` may be missing, unlike on a model's {@link StringParameter}: the engine sends a read-only one it has not filled in — `Bredde` — without the key at all. `optionalKey` says that, and sends the parameter back as it came, the way it does for a {@link LookupParameter} nobody has answered.
 *
 * `values` is there when the attribute is answered from a list rather than typed — `Farve` is `Hvid`, `Sort` or `Grå` — and missing otherwise. An empty list is still a list: the parameter is answered from one, which offers nothing.
 */
export class StockStringParameter extends StockConfigurationParameter.extend<StockStringParameter>('StockStringParameter')(
	Schema.Struct({
		type: Schema.Struct({ self: Schema.Literal(`${CONFIGURATION_TYPES_ROOT}/string`) }),
		value: Schema.optionalKey(Schema.String),
		values: Schema.optionalKey(StockAnswers),
	}),
) {
	/** The answers the parameter can be given, in the order the engine wants them offered, or null when it is typed rather than answered from a list. */
	public get answers(): ReadonlyArray<StockAnswer> | null {
		return this.values === undefined ? null : this.values.items.toSorted((first, second) => first.sortOrder - second.sortOrder);
	}

	/** The answer the parameter holds, among those it can be given, or null when it holds none of them — nobody has answered, or it is typed. */
	public get answer(): StockAnswer | null {
		const { value } = this;
		return value === undefined ? null : (this.answers?.find((answer) => answer.isAnswerTo(value)) ?? null);
	}

	/** The answer to take when nobody has answered, or null when the engine names none — or the parameter is not answered from a list. */
	public get defaultAnswer(): StockAnswer | null {
		return this.answers?.find((answer) => answer.isDefault) ?? null;
	}

	/** The same parameter answered with that text, in the state a changed parameter is sent in — see {@link answeredState}. */
	public withValue(value: string): StockStringParameter {
		return new StockStringParameter({ ...this, state: answeredState(this), value });
	}
}

/**
 * The parameters with every open question that has a default answered with it, or null when there is none to answer.
 *
 * An open question is a parameter answered from a list, with no value, and one the user may change; its default is the answer the engine marks `isDefault`. The rest go as they are. Null rather than the same list, so a caller can tell there is nothing to send.
 */
export function withDefaultAnswers(parameters: ReadonlyArray<IStockConfigurationParameter>): Array<IStockConfigurationParameter> | null {
	let answered = false;
	const items = parameters.map((parameter) => {
		if (!(parameter instanceof StockStringParameter) || parameter.value !== undefined || parameter.isReadOnly) {
			return parameter;
		}
		const fallback = parameter.defaultAnswer;
		if (fallback === null) {
			return parameter;
		}
		answered = true;
		return parameter.withValue(fallback.key);
	});
	return answered ? items : null;
}

/**
 * A stock parameter whose value is a number.
 *
 * A number on the wire and not text the way a model's measurements are sent — `600`, not `"600"` — so it is declared as one, finite since JSON has no other kind.
 *
 * `value` is missing while nobody has answered it: the engine sends the model width it needs as `pending` and without a value, which is what keeps the configuration `pending` until it is answered.
 */
export class StockNumberParameter extends StockConfigurationParameter.extend<StockNumberParameter>('StockNumberParameter')(
	Schema.Struct({
		type: Schema.Struct({ self: Schema.Literal(`${CONFIGURATION_TYPES_ROOT}/number`) }),
		value: Schema.optionalKey(Schema.Finite),
	}),
) {
	/** The same parameter answered with that number, in the state a changed parameter is sent in — see {@link answeredState}. */
	public withValue(value: number): StockNumberParameter {
		return new StockNumberParameter({ ...this, state: answeredState(this), value });
	}
}

/**
 * A stock parameter in whichever of the kinds the Configurator can present.
 *
 * Refusing any other, for the reason {@link ConfigurationParameters} refuses a model parameter it does not know: a kind with no control to put on screen is a configuration that silently cannot be saved back.
 */
export const StockConfigurationParameters = Schema.Union([StockNumberParameter, StockStringParameter]);

/** A stock parameter in whichever of the kinds the Configurator can present. */
export type IStockConfigurationParameter = typeof StockConfigurationParameters.Type;

/**
 * A parameter the answers list can show: a model's, or a stock item's.
 *
 * The two families answer to the same list, since a position is one or the other and its parameters are what the list shows when it is selected. Told apart by their classes, the way the kinds inside each family are — see {@link isStockParameter}.
 */
export type IPresentedParameter = IConfigurationParameter | IStockConfigurationParameter;

/** Whether a parameter the answers list shows is a stock item's rather than a model's. */
export function isStockParameter(parameter: IPresentedParameter): parameter is IStockConfigurationParameter {
	return parameter instanceof StockStringParameter || parameter instanceof StockNumberParameter;
}

/**
 * Which inventory item a stock configuration is of, as the engine names it in its answer.
 *
 * Prefixed and under {@link STORES_ROOT}, unlike {@link ModelSelf}: the engine answers a stock configuration with the item linked the way it links its own collections, though it was asked for one unprefixed. So this is only ever read, and the body that asks for a configuration names the item its own way.
 */
const InventoryItemSelf = Schema.TemplateLiteral([STORES_ROOT, Segment, '/component-inventory-items/', Segment]);

/** Which configuration of the item a stock configuration is, as the engine names it in its answer. Read only, for the reason {@link InventoryItemSelf} is. */
const InventoryItemConfigurationSelf = Schema.TemplateLiteral([STORES_ROOT, Segment, '/component-inventory-item-configurations/', Segment]);

/**
 * Where the configurations the item comes in are listed, as the engine links them in its answer: the store's item configurations, filtered by the item.
 *
 * Read only and prefixed, for the reason {@link InventoryItemSelf} is, and fetched as it is written: it is the engine's own link to the list, so the list is asked for where the engine says it is rather than at a path built beside it.
 */
const InventoryItemConfigurationsSelf = Schema.TemplateLiteral([STORES_ROOT, Segment, '/component-inventory-item-configurations?item=', Segment]);

/**
 * Which inventory item a stock configuration is asked for, as the body that asks for one has to name it.
 *
 * Unprefixed, the way {@link ModelSelf} names a model, and the opposite of how the engine answers — see {@link InventoryItemSelf}. Exported for the body, so the prefixed form a search answers with does not compile there.
 */
export const InventoryItemReference = Schema.TemplateLiteral(['/apps/one/stores/', Segment, '/component-inventory-items/', Segment]);

/** How an inventory item is named in the body that asks for a stock configuration. */
export type IInventoryItemReference = typeof InventoryItemReference.Type;

/** Which configuration of the item a stock configuration is asked for, named the way {@link InventoryItemReference} names the item. */
export const InventoryItemConfigurationReference = Schema.TemplateLiteral(['/apps/one/stores/', Segment, '/component-inventory-item-configurations/', Segment]);

/** How an item configuration is named in the body that asks for a stock configuration. */
export type IInventoryItemConfigurationReference = typeof InventoryItemConfigurationReference.Type;

/**
 * An inventory item configured for one stock position: which item, in which of its configurations, and the answers its parameters have.
 *
 * Its own class rather than a kind of {@link Configuration}, because what it is of is not a model: it names an item and the configuration the item comes in, and has no measurements of the position's own. The engine answers with a width and a height all the same; they are not declared, since nothing a stock position is
 * reads them.
 *
 * `parameters` keeps the envelope for the reason {@link Configuration} gives: what came back as `{ items: [...] }` goes back the same way.
 *
 * `itemConfiguration` is missing until one has been picked. A stock position is configured first as its item alone, and the engine does not pick one of the item's configurations for it: when the item comes in any, it says so with `itemConfigurations`, the link to the list they are picked from, and the configuration is
 * asked for again with the one the user picks — see {@link StockConfiguration.itemConfigurationsList}. An item that comes in none carries neither.
 */
export class StockConfiguration extends Schema.Class<StockConfiguration>('StockConfiguration')(
	Schema.Struct({
		item: Schema.Struct({ self: InventoryItemSelf }),
		itemConfiguration: Schema.optionalKey(Schema.Struct({ self: InventoryItemConfigurationSelf })),
		itemConfigurations: Schema.optionalKey(Schema.Struct({ self: InventoryItemConfigurationsSelf })),
		parameters: Schema.Struct({ items: Schema.Array(StockConfigurationParameters) }),
		quantity: PositiveInteger,
		self: ConfigurationSelf,
		status: Schema.Struct({ self: StatusSelf }),
		type: Schema.Struct({ self: Schema.Literal(`${CONFIGURATION_TYPES_ROOT}/stock`) }),
	}),
) {
	/** The identifier a later change addresses the configuration by. See {@link Configuration.id}. */
	public get id(): string {
		return lastSegment(this.self);
	}

	/** The item number of the item the configuration is of — `PERSIENNE` — which is what the position's key holds. */
	public get itemKey(): string {
		return lastSegment(this.item.self);
	}

	/** The code of the configuration the item comes in — `K001` — or null while none has been picked. */
	public get itemConfigurationKey(): string | null {
		return this.itemConfiguration === undefined ? null : lastSegment(this.itemConfiguration.self);
	}

	/** Where the configurations the item comes in are listed, or null when it comes in none — and there is nothing to pick. */
	public get itemConfigurationsList(): string | null {
		return this.itemConfigurations === undefined ? null : this.itemConfigurations.self;
	}

	/** Whether the engine will price the configuration: `complete` when every parameter has an answer, `pending` while one has not. See {@link Configuration.statusType}. */
	public get statusType(): IConfigurationStatusType {
		return this.status.self[STATUS_PART];
	}
}
